import type { Breadcrumb, BrowserOptions } from '@sentry/browser';

import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';
// import { useQuery } from '@atlassian/conversation-assistant-utils/queryUtils';

import { getRovoChatProduct } from '../../common/utils/product-config';
import { ConversationAssistantPrivateReporter } from './private-reporter';

const importPrivateSentryAsync = async () => {
	const sentry = await import(
		/* webpackChunkName: "@atlassian/conversation-assistant-instrumentation/private-sentry" */
		'@sentry/browser'
	);
	return {
		BrowserClient: sentry.BrowserClient,
		eventFromException: sentry.eventFromException,
	};
};

const DEFAULT_PACKAGE_NAME = '@atlassian/conversation-assistant';
const DEFAULT_SENTRY_DSN =
	'https://0d679a1c0fe3816e2b2e34d4ca85d5da@o55978.ingest.sentry.io/4506794008903680';

type SetupSentryClientOptions = {
	customSentryDsn?: string;
	packageName?: string;
	product?: string;
	ignoreErrors?: BrowserOptions['ignoreErrors'];
	packageVersion?: string;
};

let privateReporter: ConversationAssistantPrivateReporter | undefined;
let accountIdPromise: Promise<string> | undefined;

const defaultAccountIdProvider = (): Promise<string> =>
	new Promise((resolve, reject) => {
		performHttpFetch<{ account_id?: string }>({
			path: '/gateway/api/me',
			options: { method: 'GET' },
		})
			.then((profileData) => {
				if (profileData?.account_id) {
					resolve(profileData.account_id);
				} else {
					reject(new Error('Profile data not loaded'));
				}
			})
			.catch((error) => {
				reject(error);
			});
	});

let accountIdProvider: () => Promise<string> = defaultAccountIdProvider;

/**
 * Override the provider used by `setupSentryClient` to look up the current
 * user's account id. Intended for tests and example fixtures so they don't
 * have to mock the underlying `/gateway/api/me` HTTP call.
 *
 * Resets the cached account-id promise so the next `setupSentryClient` call
 * uses the new provider.
 */
export const setAccountIdProvider = (fn: () => Promise<string>): void => {
	accountIdProvider = fn;
	accountIdPromise = undefined;
};

/**
 * Restore the default provider (which fetches `/gateway/api/me`).
 */
export const resetAccountIdProvider = (): void => {
	accountIdProvider = defaultAccountIdProvider;
	accountIdPromise = undefined;
};

export const resetSentryClientForTests = (): void => {
	privateReporter?.resetForTests();
	privateReporter = undefined;
	accountIdPromise = undefined;
	accountIdProvider = defaultAccountIdProvider;
};

const getEnvironment = (): BrowserOptions['environment'] => {
	if (process.env.NODE_ENV !== 'production') {
		return;
	}

	if (process.env.CLOUD_ENV === 'staging') {
		return 'staging';
	}

	return 'production';
};

const getRelease = (): string | undefined => {
	if (typeof window === 'undefined') {
		return;
	}
	return (window as Window & { SENTRY_RELEASE?: { id?: string } }).SENTRY_RELEASE?.id;
};

const setupPrivateReporter = async ({
	accountId,
	customSentryDsn,
	ignoreErrors,
	packageName,
	packageVersion,
	product,
}: {
	accountId: string;
	customSentryDsn?: string;
	ignoreErrors?: BrowserOptions['ignoreErrors'];
	packageName: string;
	packageVersion?: string;
	product: string;
}): Promise<void> => {
	const dsn = customSentryDsn || DEFAULT_SENTRY_DSN;
	if (privateReporter) {
		privateReporter.updateContext({ accountId, packageVersion, product });
		return;
	}

	try {
		const importedSentry = await importPrivateSentryAsync();
		privateReporter = new ConversationAssistantPrivateReporter({
			accountId,
			dsn,
			environment: getEnvironment(),
			ignoreErrors,
			packageName,
			packageVersion,
			product,
			release: getRelease(),
			sentry: importedSentry,
		});
	} catch {
		// Private reporting is best effort and must not affect Sentry callers.
	}
};

export const setupSentryClient = async ({
	customSentryDsn,
	packageName,
	product: productOverride,
	ignoreErrors,
	packageVersion,
}: SetupSentryClientOptions = {}): Promise<void> => {
	let accountId = 'UNKNOWN_AAID';
	const product = productOverride ?? getRovoChatProduct();
	const resolvedPackageName = packageName || DEFAULT_PACKAGE_NAME;

	try {
		if (!accountIdPromise) {
			accountIdPromise = accountIdProvider();
		}
		accountId = await accountIdPromise;
	} catch {
		// do nothing
	}

	await setupPrivateReporter({
		accountId,
		customSentryDsn,
		ignoreErrors,
		packageName: resolvedPackageName,
		packageVersion,
		product,
	});
};

type Tags = {
	[key: string]: string | undefined;
	teamName?: string;
};

export const logException = async ({
	exception,
	name,
	tags,
}: {
	exception: Error | unknown;
	name: string;
	tags?: Tags;
}): Promise<void> => {
	if (!privateReporter) {
		await setupSentryClient();
	}
	try {
		await privateReporter?.captureException({ exception, name, tags });
	} catch {
		// Private reporting is best effort and must not affect Sentry callers.
	}
};

export async function addSentryBreadcrumb(crumb: string | Breadcrumb): Promise<void> {
	if (!privateReporter) {
		await setupSentryClient();
	}
	try {
		privateReporter?.addBreadcrumb(crumb);
	} catch {
		// Breadcrumb collection is best effort and must never affect the caller.
	}
}

export const logSentryConsoleError = (...args: unknown[]): void => {
	console.error(...args); // eslint-disable-line no-console
	if (privateReporter) {
		try {
			privateReporter.addBreadcrumb({
				category: 'console',
				level: 'error' as Breadcrumb['level'],
				message: args.map(String).join(' '),
			});
		} catch {
			// Mirroring selected console output is best effort.
		}
	}
};

export const logSentryConsoleWarning = (...args: unknown[]): void => {
	console.warn(...args); // eslint-disable-line no-console
	if (privateReporter) {
		try {
			privateReporter.addBreadcrumb({
				category: 'console',
				level: 'warning' as Breadcrumb['level'],
				message: args.map(String).join(' '),
			});
		} catch {
			// Mirroring selected console output is best effort.
		}
	}
};
